import * as React from 'react'
import { cn } from '@/utils/cn'

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'destructive' | 'success'
type ButtonSize = 'lg' | 'md' | 'sm' | 'xs' | 'icon'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  block?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', loading = false, block = false, disabled, children, ...props }, ref) => {
    const base = 'inline-flex items-center justify-center font-medium rounded-[var(--radius)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'

    const variants: Record<ButtonVariant, string> = {
      primary: 'bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90 active:bg-[hsl(var(--primary))]/80',
      secondary: 'bg-[var(--brand-dark)] text-white hover:bg-[var(--brand-dark)]/90',
      outline: 'border border-[hsl(var(--border))] bg-transparent hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]',
      ghost: 'bg-transparent hover:bg-[hsl(var(--muted))] text-[hsl(var(--foreground))]',
      link: 'bg-transparent underline-offset-4 hover:underline text-[hsl(var(--primary))]',
      destructive: 'bg-[hsl(var(--destructive))] text-white hover:bg-[hsl(var(--destructive))]/90',
      success: 'bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90',
    }

    const sizes: Record<ButtonSize, string> = {
      lg: 'h-12 px-6 text-base gap-2',
      md: 'h-10 px-4 text-sm gap-2',
      sm: 'h-8 px-3 text-xs gap-1.5',
      xs: 'h-6 px-2 text-xs gap-1',
      icon: 'h-10 w-10',
    }

    return (
      <button
        className={cn(base, variants[variant], sizes[size], block && 'w-full', className)}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {loading && <span className="spinner spinner-sm" />}
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
